<script setup lang="ts">
import { iconChecked } from '@/utils/backgroundIcons'
import arrowUpIcon from '@/assets/icons/blog/arrow-up.svg'
import arrowDownIcon from '@/assets/icons/blog/arrow-down.svg'
import type { TCategoryElements, TCategoryItem } from '@/ts/interfaces/BlogCategory'

const props = withDefaults(
    defineProps<{
        categorySelect: TCategoryElements
        categoriesSelected: TCategoryItem[]
    }>(),
    { categoriesSelected: () => [] },
)

const emit = defineEmits<{
    (e: 'posts', payload: string): void
    (e: 'update:categoriesSelected', payload: TCategoryItem[]): void
}>()

const { url, categories } = props?.categorySelect

const route = useRoute()
const isDropdownOpen = ref(false)
const dropdownRef = ref()

const selectedCategories = computed({
    get() {
        return props.categoriesSelected
    },
    set(value) {
        emit('update:categoriesSelected', value)
    },
})

const displayCategories = computed(() =>
    categories.filter((category) => category.name).sort((a, b) => a.name.localeCompare(b.name)),
)

function toggleCategory(category: TCategoryItem) {
    const index = selectedCategories.value.findIndex((c) => c.name === category.name)
    if (index >= 0) {
        selectedCategories.value.splice(index, 1)
    } else {
        selectedCategories.value.push(category)
    }
}

function updateUrlParams() {
    if (selectedCategories.value.length === 0) {
        const regex = /(&|\?)categoryUrlParam=[^&]*/
        let newUrl = url.replace(regex, (match, p1) => (p1 === '?' ? '?' : ''))

        newUrl = newUrl.replace(/&$/, '')
        emit('posts', newUrl)
        return
    }
    const categoryParams = selectedCategories.value
        .map((cat) => `categoryUrlParam=${encodeURIComponent(cat.categoryUrlParam)}`)
        .join('&')

    const baseUrl = url

    const fullUrl = `${baseUrl}${categoryParams ? `&${categoryParams}` : ''}`
    const sendUrl = useRemoveDuplicateQueryParams(fullUrl)
    emit('posts', sendUrl)
}

function syncCategoriesWithUrl() {
    const categoryParams = route.query.categoryUrlParam
    if (categoryParams) {
        selectedCategories.value = categories.filter((category) => categoryParams.includes(category.categoryUrlParam))
    }
}

function handleClickOutside(event: MouseEvent) {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        isDropdownOpen.value = false
    }
}

async function toggleDropdown() {
    isDropdownOpen.value = !isDropdownOpen.value
    if (isDropdownOpen.value) {
        await nextTick()
        const firstFocusableSpan = dropdownRef.value?.querySelector('[tabindex="0"]')
        firstFocusableSpan?.focus()
    }
}

function handleKeydown(event: KeyboardEvent, category: TCategoryItem) {
    if (['Enter', ' '].includes(event.key)) {
        event.preventDefault()
        toggleCategory(category)
        updateUrlParams()
    } else {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
            return
        }
        const focusableElements = Array.from(document.querySelectorAll<HTMLElement>(`[tabindex='0']`))
        const currentElement = document.activeElement as HTMLElement
        const currentIndex = focusableElements.indexOf(currentElement)
        if (currentIndex === -1) {
            return
        }
        let nextIndex = currentIndex
        if (event.key === 'ArrowDown') {
            nextIndex = (currentIndex + 1) % focusableElements.length
        } else if (event.key === 'ArrowUp') {
            nextIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length
        }
        focusableElements[nextIndex].focus()
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    syncCategoriesWithUrl()
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})

const selectedCategory = computed(() => {
    return selectedCategories.value.length > 0
        ? selectedCategories.value.map((el) => el.name).join(', ')
        : 'Wszystkie kategorie'
})
</script>

<template>
    <div
        ref="dropdownRef"
        class="flex w-full flex-col md:w-1/2 lg:w-[300px]"
    >
        <div class="relative">
            <button
                class="flex h-12 w-full items-center justify-between overflow-hidden rounded border-2 border-plus-primary px-3 text-plus-primary"
                :class="{ 'border-4': isDropdownOpen }"
                @click="toggleDropdown"
                @keyup.esc="isDropdownOpen = false"
            >
                {{ selectedCategory }}
                <AtomImage
                    :src="isDropdownOpen ? arrowUpIcon : arrowDownIcon"
                    img-class="ml-3"
                />
            </button>
            <div
                v-if="isDropdownOpen"
                class="c-scrollbar absolute z-20 mt-1 max-h-[264px] w-full overflow-y-auto rounded bg-white shadow-[0_10px_15px_-3px_rgba(83,83,83,0.3)]"
            >
                <label
                    v-for="category in displayCategories"
                    :key="category.name"
                    class="flex cursor-pointer items-center p-3 hover:bg-plus-blue"
                    @keyup="handleKeydown($event, category)"
                    @keyup.esc="isDropdownOpen = false"
                >
                    <input
                        v-model="selectedCategories"
                        type="checkbox"
                        :value="category"
                        class="hidden"
                        @change="updateUrlParams"
                    />
                    <span
                        class="relative mr-3 size-6 rounded-sm border-2 border-plus-primary bg-white"
                        :class="{ '!bg-plus-primary': selectedCategories.some((c) => c.name === category.name) }"
                        :style="{
                            backgroundImage: selectedCategories.some((c) => c.name === category.name)
                                ? `url(${iconChecked})`
                                : 'none',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                        }"
                        tabindex="0"
                    />
                    {{ category.name }}
                </label>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
button {
    border-radius: 4px;
}
</style>
